/* eslint-disable @typescript-eslint/no-unused-expressions */
import TrafficLightComponent from "./TrafficLightComponent";
import { Card } from "./ui/card";
import { useDate } from "@/Pages/StatusReport";
import { WorkstreamHealth } from "./StatusReport/StatusReportChart";
import { useNavigate } from "react-router-dom";
import { getImpactRating } from "@/lib/utils";
import { AlertComponent, RaidNotification } from "./Notifications";
import { useIsMobile } from "@/hooks/use-mobile";

export type ProjectHealthProps = {
    notifications: RaidNotification[];
    setNotifications: (notifications: RaidNotification[]) => void;
    projectHealth: WorkstreamHealth;
    currentWeek: number;
    totalWeeks: number;
    isMultiProject?: boolean;
    handleHealthClick?: (status: 1 | 2 | 4, statusName: "overall" | "scope" | "schedule" | "resources" | "financial" | "other") => void;
}

function ProjectHealth({ projectHealth: workstreamHealth, currentWeek, totalWeeks, notifications, setNotifications, isMultiProject = false, handleHealthClick }: ProjectHealthProps) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    function handleClick(status: 1 | 2 | 4, statusName: "overall" | "scope" | "schedule" | "resources" | "financial" | "other") {
        if (handleHealthClick) {
            handleHealthClick(status, statusName);
            return;
        }

        switch (statusName) {
            case "overall":
                break;
            case "scope":
                if (status === 4) {
                    navigate('/Charter');
                }
                else {
                    navigate('/Charter', {
                        state: [
                            { id: "status", value: "Open" },
                            { id: "impactRating", value: getImpactRating(status) }
                        ]
                    });
                }
                break;
            case "schedule":
                navigate('/Plan', { state: { filterCombo: status === 4 ? 0 : status * 128 } });
                break;
            case "resources":
                navigate('/SupportRequests', {
                    state: [
                        { id: "status", value: "Open" },
                        { id: "impactRating", value: getImpactRating(status) },
                        { id: "requestType", value: "Resource" }
                    ]
                });
                break;
            case "financial":
                navigate('/SupportRequests', {
                    state: [
                        { id: "requestType", value: "Financial" },
                        { id: "status", value: "Open" },
                        { id: "impactRating", value: getImpactRating(status) }
                    ]
                });
                break;
            case "other":
                navigate('/SupportRequests', {
                    state: [
                        { id: "requestType", value: "Other" },
                        { id: "status", value: "Open" },
                        { id: "impactRating", value: getImpactRating(status) }
                    ]
                });
                break;
            default:
                console.error("Invalid status name");
                break;
            }
    }

    return (
        <Card className="flex flex-col justify-center p-4 min-w-[600px] mx-auto">
            <div className="flex flex-row justify-between items-center mb-4">
                <div className={`pr-2 w-full text-right ${isMobile ? "text-2xl" : ""}`}>
                    {useDate().date} {useDate().time}
                </div>
                <AlertComponent notifications={notifications} setNotifications={setNotifications} />
            </div>

            <div className="flex flex-col items-center">

                <div className="flex flex-row w-full px-6 align-middle justify-between">
                    <div className="flex">
                        <h1 className={`font-bold ${isMobile ? "text-4xl" : "text-2xl"}`}>{isMultiProject ? "Overall" : "Project"} Health</h1>
                    </div>
                    <div className="flex">
                        <span className={`px-2 font-bold ${isMobile ? "text-4xl" : "text-2xl"}`}>Week  {currentWeek}</span>
                        <span className={`my-auto ${isMobile ? "text-xl" : ""}`}> of </span>
                        <span className={`px-2 font-bold ${isMobile ? "text-4xl" : "text-2xl"}`}>{totalWeeks}</span>
                    </div>
                </div>
            </div>

            <div className="flex flex-col border border-slate-400 rounded-xl my-2">
                <div className="flex flex-row border-b-2 border-slate-400 p-4 rounded-t-xl bg-table-header text-white font-bold">
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Overall</h3></div>
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Scope</h3></div>
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Schedule</h3></div>
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Resources</h3></div>
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Financial</h3></div>
                    <div className="flex-1 w-1/7 justify-items-center text-center"><h3 className={`px-2  ${isMobile ? "text-2xl" : ""}`}>Other</h3></div>
                </div>
                {workstreamHealth &&
                    <div className="flex flex-row p-4">
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.overall, "overall")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.overall} />
                        </div>
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.scope, "scope")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.scope} />
                        </div>
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.schedule, "schedule")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.schedule} />
                        </div>
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.resources, "resources")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.resources} />
                        </div>
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.financial, "financial")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.financial} />
                        </div>
                        <div className="flex-1 w-1/7 justify-items-center" onClick={() => handleClick(workstreamHealth.other, "other")}>
                            <TrafficLightComponent TrafficLightColour={workstreamHealth.other} />
                        </div>
                    </div>}
            </div>
        </Card>
    );
}

export default ProjectHealth;